// Shared queue-state selector for the WIN Field App (ADR-0048 §1).
//
// Single source of truth that every visibility surface reads from. Plain module
// (like field_image_queue.js), importable by Stimulus controllers and the
// service worker alike. Pure reads only -- this module NEVER mutates the queue.
//
// Two exports:
//   responseState(responseId) -- the state of one response's local queue.
//   queueSummary()            -- an aggregate across the whole queue.
//
// Backing stores (see field_db.js):
//   pending_responses: responseId, status ('pending' | 'reauth-required' | 'failed'), placeName, missionName, queuedAt
//   pending_images:    surveyableId (== responseId, String), status
//                      ('pending' | 'uploading' | 'failed' | 'uploaded'), sizeBytes

import db from "./field_db"

// State of one response's local queue. Resolves to exactly one of:
//   "not_submitted"     -- a pending_responses row exists (form not yet replayed)
//   "failed"            -- any failed pending_response (no completeUrl) OR failed pending_image
//   "completion_failed" -- card #4594: a queued completion (pending_responses row WITH a
//                          completeUrl) was rejected, or exhausted its retry cap
//   "completion_pending"-- card #4594: a completion is queued and still waiting to send
//                          (status 'pending' or 'reauth-required', completeUrl present) --
//                          the mission is DONE, just not synced yet, distinct from a plain
//                          unsubmitted save
//   "waiting"           -- any pending/uploading image (and nothing above applies)
//   "synced"            -- nothing queued for this response
//
// Precedence: failed > completion_failed > completion_pending > not_submitted > waiting > synced.
//   Failures need the user most; a rejected/stuck completion is the next-biggest gap (the
//   mission looks done locally but will never sync itself); a completion still quietly
//   waiting for signal outranks a plain unsubmitted save (it's further along); waiting
//   photos are benign-in-progress; synced is the all-clear.
//
// Returns { state, waitingImages, failedImages, responsePending } (counts are numbers).
// responsePending/responseFailed (via failedImages/responseFailed below) deliberately
// EXCLUDE completeUrl-bearing rows -- those are counted via completionPending/completionFailed
// instead, so a response doesn't double-count across two states.
export async function responseState(responseId) {
  const key = String(responseId)

  const [responseRows, waitingImages, failedImages] = await Promise.all([
    db.pending_responses.where("responseId").equals(key).toArray(),
    db.pending_images.where("surveyableId").equals(key)
      .filter(r => r.status === "pending" || r.status === "uploading").count(),
    db.pending_images.where("surveyableId").equals(key)
      .filter(r => r.status === "failed").count()
  ])

  const responsePending   = responseRows.filter(r => r.status === "pending" && !r.completeUrl).length
  const responseFailed    = responseRows.filter(r => r.status === "failed" && !r.completeUrl).length
  const completionPending = responseRows.some(r => !!r.completeUrl && (r.status === "pending" || r.status === "reauth-required"))
  const completionFailed  = responseRows.some(r => !!r.completeUrl && r.status === "failed")

  let state
  if (failedImages > 0 || responseFailed > 0) {
    state = "failed"
  } else if (completionFailed) {
    state = "completion_failed"
  } else if (completionPending) {
    state = "completion_pending"
  } else if (responsePending > 0) {
    state = "not_submitted"
  } else if (waitingImages > 0) {
    state = "waiting"
  } else {
    state = "synced"
  }

  return { state, waitingImages, failedImages, responsePending }
}

// Aggregate across the entire queue -- for the header CTA, all-clear panel, and
// storage gauge (later phases). One .toArray() per table at most.
//
// Returns:
//   { pendingResponses, failedResponses, pendingImages, uploadingImages, failedImages,
//     attentionImages, reauthRequired, latestUploadStartedAt, latestUploadedAt, storeCount, pendingBytes, total, hasFailures, allClear }
//   allClear === (total === 0 && !hasFailures)
//
// attentionImages (card #3863): the failed photos that need the tasker -- lost
// (unreadable) or rejected by the server. A row an old build failed after
// network errors (failStatus 0) is re-armed on the next pass, so it is still
// counted in failedImages but not here.
// reauthRequired (card #3863): photos AND surveys parked until the tasker
// signs in again (field_upload_sync.js marks a survey "reauth-required" when
// the session expires mid-sync). They sit outside total/allClear, as before:
// those feed the existing banner and header, which treat them as neither
// waiting nor failed. Any surface that claims "all saved" must also check
// reauthRequired === 0.
// latestUploadStartedAt (card #3863): epoch ms of the most recent
// uploadStartedAt among "uploading" photos, or 0. Lets a surface tell a live
// upload from a row left "uploading" after the app was killed.
// latestUploadedAt (card #3863): epoch ms of the most recent uploadedAt on a
// queued (not shadow) photo the server confirmed, or 0. A photo that uploads
// becomes "uploaded" rather than being deleted, so this is how a surface tells
// "the queue emptied because it was sent" from "a row was purged".
export async function queueSummary() {
  const [responses, images] = await Promise.all([
    db.pending_responses.toArray(),
    db.pending_images.toArray()
  ])

  let pendingResponses = 0
  let failedResponses  = 0
  let reauthRequired   = 0
  for (const r of responses) {
    if (r.status === "failed") failedResponses++
    else if (r.status === "pending") pendingResponses++
    else if (r.status === "reauth-required") reauthRequired++
  }

  let pendingImages   = 0
  let uploadingImages = 0
  let failedImages    = 0
  let attentionImages = 0
  let pendingBytes    = 0
  let latestUploadStartedAt = 0
  let latestUploadedAt      = 0
  for (const img of images) {
    if (img.status === "pending") {
      pendingImages++
      pendingBytes += img.sizeBytes || 0
    } else if (img.status === "uploading") {
      uploadingImages++
      pendingBytes += img.sizeBytes || 0
      const startedAt = img.uploadStartedAt ? new Date(img.uploadStartedAt).getTime() : 0
      if (startedAt > latestUploadStartedAt) latestUploadStartedAt = startedAt
    } else if (img.status === "failed") {
      failedImages++
      if (img.failStatus !== 0) attentionImages++
    } else if (img.status === "reauth-required") {
      reauthRequired++
    } else if (img.status === "uploaded" && img.kind !== "shadow" && img.uploadedAt) {
      const uploadedAt = new Date(img.uploadedAt).getTime()
      if (uploadedAt > latestUploadedAt) latestUploadedAt = uploadedAt
    }
  }

  // Distinct stores/missions with anything queued. We group on the response
  // identity, not on placeName text: pending_responses carry placeName but
  // pending_images carry only surveyableId (the response id), so placeName is
  // unavailable for image-only queues. Counting distinct response ids gives a
  // stable, surface-agnostic "how many stores have outstanding work" -- one
  // response is one store visit. (A future surface that wants display names can
  // join placeName back in from pending_responses.)
  const storeIds = new Set()
  for (const r of responses) {
    if (r.status === "pending" || r.status === "failed") storeIds.add(String(r.responseId))
  }
  for (const img of images) {
    if (img.status === "pending" || img.status === "uploading" || img.status === "failed") {
      storeIds.add(String(img.surveyableId))
    }
  }

  const total = pendingResponses + failedResponses + pendingImages + uploadingImages + failedImages
  const hasFailures = failedResponses > 0 || failedImages > 0

  return {
    pendingResponses,
    failedResponses,
    pendingImages,
    uploadingImages,
    failedImages,
    attentionImages,
    reauthRequired,
    latestUploadStartedAt,
    latestUploadedAt,
    storeCount: storeIds.size,
    pendingBytes,
    total,
    hasFailures,
    allClear: total === 0 && !hasFailures
  }
}

// Returns true if this response has been pre-cached in IndexedDB and is
// available to work on offline (IndexedDB cached_responses table).
export async function isCachedOffline(responseId) {
  try {
    const count = await db.cached_responses
      .where("responseId").equals(Number(responseId))
      .count()
    return count > 0
  } catch {
    return false
  }
}
